import { classify, selectRelated, recommendationRank, sameGender, productGroup, diversify } from '@theme/excello-match';


import { interleave, parallelMap } from '@theme/excello-family';
import { previewUrl } from '@theme/excello-preview';
export { previewUrl };

const feeds = new Map();
const cards = new Map();
const historyKey = 'excello-recommendations-history-v2';
let instance = 0;

const parseData = node => JSON.parse(node.textContent);
const eligible = item => item && item.available && !item.hidden;
const fetchMarkup = async url => {
  const response = await fetch(previewUrl(url), { credentials: 'same-origin', priority: 'low' });
  if (!response.ok) throw new Error('Recommendation request failed');
  return new DOMParser().parseFromString(await response.text(), 'text/html');
};

function historyAllowed() {
  return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.() === true;
}

function readHistory(scope = 'guest') {
  if (!historyAllowed()) return [];
  try {
    const now = Date.now();
    return JSON.parse(localStorage.getItem(`${historyKey}:${scope}`) || (scope === 'guest' ? localStorage.getItem('excello-recommendations-history-v1') : null) || '[]').filter(item => /^[a-z0-9-]+$/.test(item.handle) && Number.isFinite(item.time) && item.time <= now && now - item.time < 365 * 86400000).sort((a,b)=>b.time-a.time);
  } catch { return []; }
}

class ExcelloProductJourney extends HTMLElement {
  connectedCallback() {
    if (this.started) return;
    this.started = true;
    this.connection = new AbortController();
    this.current = classify(parseData(this.querySelector('[data-rec-current]')));
    this.shown = new Set();
    this.root = this.dataset.root.endsWith('/') ? this.dataset.root : `${this.dataset.root}/`;
    this.categoryHandle = { oversized: 'oversized-t-shirt', regular: 'crew-neck', polo: 'polo-t-shirt', hoodie: 'hoodies', sweatshirt: 'sweatshirt', shorts: 'shorts', accessories: 'accessories', combo: 'combo' }[this.current.category];
    this.prepareHistory();
    this.initializing = false;
    const start = () => {
      if(this.initializing || this.connection.signal.aborted)return;
      this.initializing = true;
      this.observer?.disconnect();
      this.initialize().catch(()=>{this.dataset.ready='error';});
    };
    this.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.observer.disconnect();
        start();
      }
    }, { rootMargin: '400px 0px' });
    this.observer.observe(this);
    if('requestIdleCallback' in window)this.idleStart=window.requestIdleCallback(start,{timeout:1200});
    else this.deferredStart=setTimeout(start,300);
  }

  disconnectedCallback() {
    if(this.idleStart!==undefined)window.cancelIdleCallback?.(this.idleStart);
    clearTimeout(this.deferredStart);
    this.observer?.disconnect();
    this.moduleObserver?.disconnect();
    this.recentObserver?.disconnect();
    for(const observer of this.renderObservers?.values()||[])observer.disconnect();
    this.connection?.abort();
    this.started = false;
    document.removeEventListener('visitorConsentCollected', this.consentHandler);
  }

  requestUrl(path, section, page) {
    const url = new URL(`${this.root}${path}`, location.origin);
    url.searchParams.set('section_id', section);
    if (page) url.searchParams.set('page', page);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }

  loadCollection(handle) { return loadCollection(handle,this.root); }

  loadCard(handle, context = '') { return loadCard(handle,context,this.root); }

  async initialize() {
    const categoryRequest = this.categoryHandle ? this.loadCollection(this.categoryHandle) : Promise.resolve([]);
    const comboRequest = this.loadCollection('combo');
    categoryRequest.catch(()=>{});comboRequest.catch(()=>{});
    this.categoryRequest=categoryRequest;this.comboRequest=comboRequest;
    const sets = await Promise.allSettled([this.loadCollection(this.dataset.best), this.loadCollection(this.dataset.deals)]);
    this.best = sets[0].status === 'fulfilled' ? sets[0].value.filter(item=>sameGender(this.current,item)) : [];
    this.deals = sets[1].status === 'fulfilled' ? sets[1].value.filter(item=>sameGender(this.current,item)) : [];
    for (const [key, items] of [['best', this.best], ['deals', this.deals]]) {
      if (items.some(item => item.id !== this.current.id)) this.module(key).hidden = false;
    }
    this.moduleObserver = new IntersectionObserver(entries => {
      for (const entry of entries) if (entry.isIntersecting) {
        this.moduleObserver.unobserve(entry.target);
        const key = entry.target.dataset.module;
        this.render(key, key === 'best' ? this.best : this.deals);
      }
    }, { rootMargin: '300px 0px' });
    for (const key of ['best', 'deals']) if (!this.module(key).hidden) this.moduleObserver.observe(this.module(key));
    await Promise.allSettled([this.loadRelated(), this.renderRecent()]);
    this.dataset.ready = 'true';
  }

  module(key) { return this.querySelector(`[data-module="${key}"]`); }

  async loadRelated() {
    let pool = await this.categoryRequest;
    const merchandising = new Set([...this.best, ...this.deals].map(item => item.id));
    let selected = selectRelated(this.current, pool, merchandising);
    if (selected.length < 12 || selected.some(item => recommendationRank(this.current, item)[1] > 3)) {
      pool = [...pool, ...await this.loadCollection('all')];
      selected = selectRelated(this.current, pool, merchandising);
    }
    const ranked = selectRelated(this.current, pool, merchandising, pool.length);
    this.dataset.matchLevels = selected.map(item => recommendationRank(this.current, item)[1]).join(',');
    await this.render('related', ranked, 10);
    const combos=(await this.comboRequest.catch(()=>[])).filter(item=>sameGender(this.current,item) && item.id!==this.current.id);
    await this.render('combos',combos);
    const omit = new Set([...this.shown,...this.best.map(x=>x.id),...this.deals.map(x=>x.id),...combos.map(x=>x.id),...(this.history||[]).map(x=>x.handle)]);
    const distinct = ranked.filter(item=>!omit.has(item.id)&&!omit.has(item.handle));
    const extra = [...distinct, ...ranked.filter(item=>!distinct.some(other=>other.id===item.id))];
    await this.render('extra',extra,2);
    await this.prepareNavigation(pool);
  }

  async render(key, items, limit = Infinity) {
    const module = this.module(key), list = module.querySelector('[data-rec-list]');
    const eligibleItems = (key === 'recent' ? items : interleave(items)).filter(item => eligible(item) && item.id !== this.current.id && (key === 'recent' || sameGender(this.current,item)));
    this.renderObservers ||= new Map();
    this.renderObservers.get(key)?.disconnect();
    list.replaceChildren();
    let offset=0,busy=false;
    const carousel=['best','deals','combos'].includes(key),rendered=new Set();
    const loadNext=async()=>{
      if(busy || this.connection.signal.aborted)return;
      busy=true;list.querySelector('[data-rec-next]')?.remove();
      const count=carousel?6:Math.min(limit,list.childElementCount+eligibleItems.length);
      const target=carousel?Math.min(limit,list.childElementCount+count):limit;
      while(offset<eligibleItems.length && list.childElementCount<target){
        const batch=await parallelMap(eligibleItems.slice(offset,offset+4),item=>this.loadCard(item.handle,key==='best'?this.dataset.best:key==='deals'?this.dataset.deals:'').catch(()=>null),4);
        offset+=4;
        if(this.connection.signal.aborted || (key==='recent'&&!historyAllowed())){busy=false;return;}
        for(const result of batch){
          if(list.childElementCount>=limit)break;
          if(!result || rendered.has(result.item.id) || result.item.id===this.current.id || (key!=='recent'&&!sameGender(this.current,classify(result.item))))continue;
          const li=document.createElement('li');li.dataset.productId=result.item.id;li.dataset.family=result.item.family||'';li.innerHTML=result.html;
          this.uniqueIds(li);list.append(li);rendered.add(result.item.id);this.shown.add(result.item.id);
        }
        if(list.childElementCount)module.hidden=false;
      }
      module.hidden=list.childElementCount===0 || (key==='recent'&&!historyAllowed());
      module.dataset.count=list.childElementCount;busy=false;
      if(offset<eligibleItems.length && list.childElementCount<limit && carousel){
        const sentinel=document.createElement('li');sentinel.className='excello-recent-next';sentinel.dataset.recNext='';sentinel.textContent='…';list.append(sentinel);
        const observer=new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting)){observer.disconnect();loadNext();}},{root:list,rootMargin:'180px'});
        this.renderObservers.set(key,observer);observer.observe(sentinel);
      }
    };
    await loadNext();
  }

  uniqueIds(element) {
    const suffix = `-rec-${++instance}`;
    for (const node of element.querySelectorAll('[id]')) {
      const old = node.id;
      node.id = `${old}${suffix}`;
      for (const target of element.querySelectorAll('[filter]')) if (target.getAttribute('filter') === `url(#${old})`) target.setAttribute('filter', `url(#${node.id})`);
    }
  }

  prepareHistory() {
    this.consentHandler = () => {
      if (!historyAllowed()) {
        try { localStorage.removeItem(`${historyKey}:${this.dataset.shopper}`); } catch {}
        this.module('recent').hidden = true;
        return;
      }
      this.recordHistory();
      if (this.dataset.ready) this.renderRecent();
    };
    document.addEventListener('visitorConsentCollected', this.consentHandler);
    if (window.Shopify?.customerPrivacy) this.recordHistory();
    else window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], error => { if (!error) this.consentHandler(); });
  }

  recordHistory() {
    if (!historyAllowed()) return;
    const prior = readHistory(this.dataset.shopper).filter(item => item.handle !== this.current.handle);
    this.history = prior;
    try { localStorage.setItem(`${historyKey}:${this.dataset.shopper}`, JSON.stringify([{ handle: this.current.handle, time: Date.now() }, ...prior])); } catch {}
  }

  async renderRecent() {
    if (!historyAllowed()) return;
    const history = this.history || readHistory(this.dataset.shopper).filter(item => item.handle !== this.current.handle);
    this.recentHandles=[...new Set(history.map(item=>item.handle))];
    this.recentItems=[];
    this.recentOffset=0;
    const loadNext=async()=>{
      const batch=this.recentHandles.slice(this.recentOffset,this.recentOffset+8);
      this.recentOffset+=batch.length;
      const results=await Promise.all(batch.map(handle=>this.loadCard(handle).catch(()=>null)));
      if(!historyAllowed()||this.connection.signal.aborted)return;
      this.recentItems.push(...results.filter(Boolean).map(result=>result.item).filter(item=>item.id!==this.current.id));
      if(!this.recentItems.length && this.recentOffset<this.recentHandles.length){await loadNext();return;}
      await this.render('recent',this.recentItems);
      const list=this.module('recent').querySelector('[data-rec-list]');
      if(this.recentOffset<this.recentHandles.length){
        const sentinel=document.createElement('li');sentinel.className='excello-recent-next';sentinel.textContent=this.querySelector('[data-navigation-copy]')?'…':'';list.append(sentinel);
        this.recentObserver?.disconnect();
        this.recentObserver=new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting)){this.recentObserver.disconnect();loadNext();}},{root:list,rootMargin:'200px'});
        this.recentObserver.observe(sentinel);
      }
    };
    await loadNext();
  }

  async prepareNavigation(pool) {
    if(!this.current.gender || !this.categoryHandle)return;
    const words=parseData(this.querySelector('[data-navigation-copy]'));
    const group=productGroup(this.current),category=words[this.current.category],allCategory=words[group]||category;
    let colour=this.current.colour;
    let matches=pool.filter(item=>eligible(item)&&sameGender(this.current,item)&&productGroup(item)===group);
    if(!matches.some(item=>item.colour===colour)) colour=matches.find(item=>item.colour)?.colour||'';
    const url=(handle,fit='',colour='')=>{
      const target=new URL(`${this.root}collections/${handle}`,location.origin);
      target.searchParams.set('view','excello-category');target.searchParams.set('excello_gender',this.current.gender);target.searchParams.set('excello_type',group);
      if(fit)target.searchParams.set('excello_fit',fit);if(colour)target.searchParams.set('excello_colour',colour);
      return target.pathname+target.search;
    };
    const setup=(selector,href,label)=>{const link=this.querySelector(selector);link.href=href;link.querySelector('span').textContent=label;link.hidden=false;};
    setup('[data-rec-fit]',url(this.categoryHandle,this.current.category),words.more.replace('__category__',category));
    if(colour)setup('[data-rec-colour]',url(group==='tee'?'all':this.categoryHandle,'',colour),words.colour.replace('__category__',allCategory).replace('__colour__',colour.replace(/\b\w/g,x=>x.toUpperCase())));
    else if(this.current.collections.includes('combo')) setup('[data-rec-colour]',url('combo',this.current.category),words.more.replace('__category__',words.combo));
    else setup('[data-rec-colour]',url(this.categoryHandle,this.current.category),words.more.replace('__category__',category));
    setup('[data-rec-all]',`${this.root}pages/category?view=explore-categories`,words.all);
    this.querySelector('[data-rec-navigation]').hidden=false;
  }
}

if (!customElements.get('excello-product-journey')) customElements.define('excello-product-journey', ExcelloProductJourney);

export function loadCard(handle, context='', root=window.Shopify?.routes?.root||'/') {
  if(!/^[a-z0-9-]+$/.test(handle))return Promise.resolve(null);
  const key=`${handle}:${context}`;
  if(cards.has(key))return cards.get(key);
  const promise=(async()=>{
    const url=new URL(`${root}products/${handle}`,location.origin);url.searchParams.set('section_id','excello-recommendation-card');
    const preview=new URL(location.href).searchParams.get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);
    const doc=await fetchMarkup(url),data=doc.querySelector('[data-rec-data]'),template=doc.querySelector('[data-rec-card]');
    if(!data||!template)return null;
    const item=parseData(data);return eligible(item)?{item,html:template.innerHTML}:null;
  })();cards.set(key,promise);promise.catch(()=>cards.delete(key));return promise;
}

export function loadCollection(handle,root=window.Shopify?.routes?.root||'/') {
  if(!handle || !/^[a-z0-9-]+$/.test(handle))return Promise.resolve([]);
  const key=`${root}:${handle}`;
  if(feeds.has(key))return feeds.get(key);
  const promise=(async()=>{
    const page=async number=>{
      const url=new URL(`${root}collections/${handle}`,location.origin);url.searchParams.set('section_id','excello-recommendation-feed');url.searchParams.set('page',number);
      const preview=new URL(location.href).searchParams.get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);
      const doc=await fetchMarkup(url),feed=doc.querySelector('[data-rec-feed]');if(!feed)throw new Error('Recommendation feed missing');
      const items=[];
      for(const entry of feed.querySelectorAll('[data-rec-entry]')){
        const item=parseData(entry.querySelector('[data-rec-data]'));if(!eligible(item))continue;
        const card=entry.querySelector('[data-rec-card]');
        if(card)cards.set(`${item.handle}:${handle}`,Promise.resolve({item,html:card.innerHTML}));
        items.push(classify(item));
      }
      return {items,pages:Number(feed.dataset.pages)||1};
    };
    const first=await page(1);
    const remaining=await parallelMap(Array.from({length:first.pages-1},(_,index)=>index+2),page,3);
    return [...new Map([first,...remaining].flatMap(result=>result.items).map(item=>[item.id,item])).values()];
  })();feeds.set(key,promise);promise.catch(()=>feeds.delete(key));return promise;
}

